import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { useScrollContext } from '../../context/ScrollContext';

/**
 * ScrollReveal — Progressive disclosure wrapper for narrative beats
 * Wraps sections to trigger entrance animations when they enter viewport
 * Respects prefers-reduced-motion and uses narrative easing curves
 */
const ScrollReveal = ({
  children,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  once = true,
  delay = 0,
  stagger = 0,
  className = '',
  style,
  as: Component = 'section',
  ...props
}) => {
  const { reducedMotion, isBeatActive: storyIsBeatActive, isBeatCompleted: storyIsBeatCompleted } = useStory();
  const { isBeatActive: contextIsBeatActive, isBeatCompleted: contextIsBeatCompleted, registerBeat, unregisterBeat } = useScrollContext();
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const beatRef = useRef(beatId);

  // Update beat ref when beatId changes
  useEffect(() => {
    beatRef.current = beatId;
  }, [beatId]);

  // Register with centralized ScrollContext
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !beatId) return;

    registerBeat(beatId, element);
    return () => unregisterBeat(beatId);
  }, [beatId, registerBeat, unregisterBeat]);

  // Use context-based visibility instead of local IntersectionObserver
  useEffect(() => {
    if (!beatId) return;

    const visible = contextIsBeatActive(beatId);
    setIsVisible(visible);
  }, [beatId, contextIsBeatActive]);

  // Check if this beat is active/completed for narrative highlighting
  // Prefer context, fall back to StoryContext
  const isCurrentBeat = beatId ? (contextIsBeatActive(beatId) || storyIsBeatActive(beatId)) : false;
  const isPastBeat = beatId ? (contextIsBeatCompleted(beatId) || storyIsBeatCompleted(beatId)) : false;

  // Reduced motion: skip animation, just show content
  if (reducedMotion) {
    return (
      <Component
        ref={elementRef}
        className={`scroll-reveal ${isVisible ? 'is-visible' : ''} ${className}`}
        style={{ opacity: isVisible ? 1 : 0, ...style }}
        {...props}
      >
        {children}
      </Component>
    );
  }

  // Framer Motion variant definitions using Apple Design principles
  // Critically damped spring: damping 1.0, response 0.4s (duration ~0.4)
  // Maps to Framer Motion: type: 'spring', bounce: 0, duration: 0.4
  const variants = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: 'blur(8px)',
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        bounce: 0, // critically damped = no overshoot
        duration: 0.4, // Apple's response: 0.4s
        delay: delay / 1000,
      },
    },
    beatActive: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        bounce: 0,
        duration: 0.35,
      },
    },
  };

  // Stagger children if stagger prop provided
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger / 1000,
        delayChildren: delay / 1000,
      },
    },
  };

  const childVariants = {
    hidden: { opacity: 0, y: 16, filter: 'blur(4px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        bounce: 0, // critically damped
        duration: 0.35,
      },
    },
  };

  const MotionComponent = motion[Component];

  return (
    <MotionComponent
      ref={elementRef}
      className={`scroll-reveal ${isCurrentBeat ? 'beat-active' : ''} ${isPastBeat ? 'beat-completed' : ''} ${className}`}
      style={style}
      variants={stagger > 0 ? containerVariants : variants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      exit="hidden"
      {...props}
    >
      {stagger > 0 && React.Children.map(children, (child, index) =>
        React.isValidElement(child) ? React.cloneElement(child, {
          variants: childVariants,
        }) : child
      )}
      {!stagger && children}
    </MotionComponent>
  );
};

/**
 * ScrollRevealText — Specialized version for text content with line-by-line reveal
 */
export const ScrollRevealText = ({
  children,
  beatId,
  threshold = 0.1,
  rootMargin = '0px',
  delay = 0,
  stagger = 100,
  className = '',
  style,
  splitBy = 'paragraph', // 'paragraph' | 'line' | 'word'
  ...props
}) => {
  const { reducedMotion } = useStory();
  const { isBeatActive: contextIsBeatActive, registerBeat, unregisterBeat } = useScrollContext();
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  // Register with centralized ScrollContext
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !beatId) return;

    registerBeat(beatId, element);
    return () => unregisterBeat(beatId);
  }, [beatId, registerBeat, unregisterBeat]);

  // Use context-based visibility
  useEffect(() => {
    if (!beatId) return;

    const visible = contextIsBeatActive(beatId);
    setIsVisible(visible);
  }, [beatId, contextIsBeatActive]);

  if (reducedMotion) {
    return (
      <div
        ref={elementRef}
        className={`scroll-reveal-text ${isVisible ? 'is-visible' : ''} ${className}`}
        style={{ opacity: isVisible ? 1 : 0, ...style }}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Split text into lines/paragraphs for stagger
  const splitText = (text) => {
    if (splitBy === 'line') return text.split('\n').filter(Boolean);
    if (splitBy === 'word') return text.split(' ').filter(Boolean);
    return text.split('\n\n').filter(Boolean); // paragraph
  };

  const textContent = typeof children === 'string' ? children : '';
  const segments = splitText(textContent);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: stagger / 1000,
        delayChildren: delay / 1000,
      },
    },
  };

  const segmentVariants = {
    hidden: { opacity: 0, y: 16, filter: 'blur(4px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        bounce: 0, // critically damped
        duration: 0.35,
      },
    },
  };

  return (
    <motion.div
      ref={elementRef}
      className={`scroll-reveal-text ${className}`}
      style={style}
      variants={containerVariants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      {...props}
    >
      {segments.map((segment, index) => (
        <motion.p
          key={index}
          variants={segmentVariants}
          className="reveal-segment"
          style={{ margin: splitBy === 'paragraph' ? '1rem 0' : splitBy === 'line' ? '0.5rem 0' : 0 }}
        >
          {segment}
        </motion.p>
      ))}
      {typeof children !== 'string' && children}
    </motion.div>
  );
};

export { ScrollReveal };
export default ScrollReveal;